/**
 * ADMIN — the ONLY operator-exclusive surface.
 *
 * The system runner's view: fleet, mesh, venue + gateway + margin pins,
 * firehose, the secrets inventory (PRESENCE ONLY, grouped by whether THIS
 * NODE'S ROLE reads them), the compactor's own health, the tenant roster and
 * the updater socket.
 *
 * Nothing here is another tenant's cases, positions, latency or money —
 * those are not admin data, they are the tenant's, and no admin endpoint
 * returns them. The route is guarded on the client AND on every server route
 * AND independently by `queries.nq`; this component is the third of three,
 * never the only one.
 *
 * S8 VENUE & CUTOVER is a SURFACE WITH AN EXPIRY DATE — it is deleted at
 * cutover. Designing for deletion is a stated principle, because the old
 * dashboard still carries a Telegram first-login wizard that is legacy twice
 * over.
 *
 * Six cards, each its own `.surface`, laid out as a grid by CSS at width.
 * There is NO all-systems-OK badge on this surface: a healthy node renders
 * as plain text, and an unhealthy one is marked by form (`--bad`), because
 * green-for-ok is the treatment that teaches an operator to stop reading.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { age, ageSince, stamp } from "../lib/time";
import { VirtualList } from "../lib/VirtualList";

export function Admin({ admin }: { admin: boolean }) {
  if (!admin) {
    // Fail-closed and quiet: a non-admin is not told what is behind it.
    return <div className="stub stub--error">not available for this principal</div>;
  }
  return (
    <div className="admin">
      <Fleet />
      <Venue />
      <Secrets />
      <IndexCard />
      <Tenants />
      <Updater />
    </div>
  );
}

/* ---------------------------------------------------------------- fleet */

/** No green, no red. `ok` and `degraded` are plain; the three states that
 *  mean "this node is not doing its job" get the one marked treatment. */
const LADDER_CLASS: Record<string, string> = {
  banned: "ladder-state ladder-state--bad",
  stale: "ladder-state ladder-state--bad",
  not_polling: "ladder-state ladder-state--bad",
  degraded: "ladder-state",
  ok: "ladder-state",
  no_data: "ladder-state dim",
};

function Fleet() {
  const q = useQuery({ queryKey: ["admin", "fleet"], queryFn: api.admin.fleet });
  const mesh = useQuery({ queryKey: ["admin", "mesh"], queryFn: api.admin.mesh });
  const conds = useQuery({ queryKey: ["admin", "conditions"], queryFn: api.admin.conditions });
  const nodes: any[] = q.data?.nodes ?? [];
  return (
    <section className="surface surface--wide">
      <header className="surface__head">
        <h2 className="surface__title">Fleet</h2>
        <div className="surface__meta mono">
          {nodes.length} node{nodes.length === 1 ? "" : "s"}
        </div>
      </header>

      <div className="surface__body">
        <p
          className="note"
          title="heartbeat age is computed from the TRADER's ingest clock, so a skewed detector cannot mislabel itself healthy. A configured peer that is absent renders DOWN, not missing; an unexpected reporting node is surfaced, never dropped."
        >
          ladder: <span className="mono">{(q.data?.ladder ?? []).join(" > ")}</span>
        </p>

        <VirtualList
          rows={nodes}
          ariaLabel="fleet nodes"
          className="vlist vlist--nodes"
          estimateSize={44}
          rowKey={(n: any) => n.node_id}
          renderRow={(n: any) => (
            <div className="noderow mono">
              <span className="noderow__id">{n.node_id}</span>
              <span className={LADDER_CLASS[n.status] ?? "ladder-state"}>{n.status}</span>
              <span className="dim">read {ageSince(n.read_at)} ago</span>
              {n.expected === 0 && <span className="chip">unexpected reporter</span>}
            </div>
          )}
          emptyLabel="no node samples ingested"
        />

        <section className="band">
          <div className="band__title">MESH</div>
          <div className="mono">{mesh.data?.sample ? mesh.data.sample.state : "no sample"}</div>
          <p className="note">{mesh.data?.note}</p>
        </section>

        <section className="band">
          <div className="band__title">CONDITIONS · full per-node detail</div>
          {(conds.data?.conditions ?? []).map((c: any) => (
            <details className="disclose" key={c.name}>
              <summary>
                <span className="mono">
                  {c.name} · {c.state} · read {ageSince(c.read_at)} ago
                </span>
              </summary>
              <pre className="mono raw__pre">{c.detail_json ?? "(no detail)"}</pre>
            </details>
          ))}
        </section>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- venue */

function Venue() {
  const q = useQuery({ queryKey: ["admin", "venue"], queryFn: api.admin.venue });
  const fire = useQuery({ queryKey: ["admin", "firehose"], queryFn: api.admin.firehose });
  return (
    <section className="surface surface--wide">
      <header className="surface__head">
        <h2 className="surface__title">Venue &amp; cutover</h2>
        <div className="surface__meta mono">deleted at cutover</div>
      </header>

      <div className="surface__body">
        {(q.data?.venues ?? []).map((v: any) => (
          <section key={v.venue} className="venue">
            <div className="band__title">{String(v.venue).toUpperCase()}</div>
            {/* The TRUST RECORD: acked-then-dropped as a RATE, computed from
                journals. The fork's own justification rendered as a statistic
                instead of remembered as folklore. */}
            <div className="figs">
              <div className="fig">
                <span className="fig__k">orders</span>
                <span className="fig__v mono num">{v.orders}</span>
              </div>
              <div className="fig">
                <span className="fig__k">acked-then-dropped</span>
                <span className="fig__v mono num">{v.acked_then_dropped}</span>
              </div>
              <div className="fig">
                <span className="fig__k">rate</span>
                <span className="fig__v mono num">
                  {v.acked_then_dropped_rate === null ? "—" : `${(v.acked_then_dropped_rate * 100).toFixed(1)}%`}
                </span>
              </div>
              <div className="fig">
                <span className="fig__k">last drop</span>
                <span className="fig__v mono">{v.last_drop ? stamp(v.last_drop) : "—"}</span>
              </div>
              <div className="fig">
                <span className="fig__k">ack measure</span>
                <span className="fig__v mono">{v.ack_measure}</span>
              </div>
            </div>
            <p className="note" title="the two venues never share an axis">
              comparable across venues: <span className="mono">{String(v.comparable_across_venues)}</span>
            </p>

            <div className="band__title">GATEWAY</div>
            {v.gateway ? (
              <div className="mono">
                {v.gateway.state}
                {v.gateway.note ? ` · ${v.gateway.note}` : ""}
              </div>
            ) : (
              <p className="note" title="the probe signals only through container restart churn (P13: gateway_health.json)">
                <span className="mono">∅</span> not journaled
              </p>
            )}

            <div className="band__title">MARGIN PINS</div>
            {Array.isArray(v.pins) && v.pins.length > 0 ? (
              v.pins.map((p: any) => (
                <div key={p.contract} className="mono">
                  {p.contract} ${p.margin_usd} · pinned {age(p.age_s)} / 30 d
                  {p.age_s > 30 * 86400 && (
                    <span className="chip chip--down" title="a stale pin sizes WRONG SILENTLY">
                      STALE
                    </span>
                  )}
                </div>
              ))
            ) : (
              <p
                className="note"
                title="pins live in the execution plane's contract specs and are not journaled per-read. A stale pin sizes WRONG SILENTLY: the divergence check only alerts, and the pin wins."
              >
                <span className="mono">∅</span> no pin rows on this endpoint
              </p>
            )}
          </section>
        ))}
        <p className="note">{q.data?.rule}</p>

        <section className="band">
          <div className="band__title">FIREHOSE</div>
          <div className="mono">{fire.data?.sample ? fire.data.sample.state : "no sample"}</div>
          <p className="note">{fire.data?.journal_note}</p>
          <p className="note">{fire.data?.keys_note}</p>
        </section>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- secrets */

const GROUP_TITLE: Record<string, string> = {
  decision: "DECISION — read by this node",
  execution_capital: "EXECUTION · capital",
  execution_ibkr: "EXECUTION · ibkr",
  mesh: "MESH",
  notify: "NOTIFY",
  not_used_by_this_role: "NOT USED BY THIS ROLE",
};

function Secrets() {
  const q = useQuery({ queryKey: ["admin", "secrets"], queryFn: api.admin.secrets });
  const groups: Record<string, any[]> = q.data?.groups ?? {};
  return (
    <section className="surface surface--wide">
      <header className="surface__head">
        <h2 className="surface__title">Secrets</h2>
        <div className="surface__meta mono">presence only, never values</div>
      </header>

      <div className="surface__body">
        <p
          className="note"
          title="a mesh-only trader's TRUTHSOCIAL_*, AXIOS_*, FLARESOLVERR_* and the Telegram read credentials collapse into 'not used by this role' rather than posing as required"
        >
          grouped by whether this node&apos;s role reads them.
        </p>

        {Object.entries(groups).map(([g, entries]) => (
          <section key={g} className={`secgroup${g === "not_used_by_this_role" ? " secgroup--unused" : ""}`}>
            <div className="band__title">{GROUP_TITLE[g] ?? g}</div>
            {(entries ?? []).map((e: any) => (
              <div key={e.name} className="mono secret">
                <span className={`secret__state${e.present ? "" : " dim"}`}>{e.present ? "set" : "unset"}</span>{" "}
                {e.name}
                {/* set-but-EMPTY is not unset. An empty VAULT_DOMAIN yields an
                    empty Caddy site address and takes ALL ingress down —
                    dashboard, /mesh, /signal and /docs together. */}
                {e.set_but_empty && <span className="chip chip--down">SET BUT EMPTY</span>}
                {e.warning && <span className="note">{e.warning}</span>}
              </div>
            ))}
          </section>
        ))}

        <p className="note">
          values are never returned by the API: <span className="mono">{String(q.data?.values_never_returned)}</span>
        </p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- index */

function IndexCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "index"], queryFn: api.admin.index });
  const rebuild = useMutation({
    mutationFn: api.admin.rebuild,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "index"] }),
  });
  const m = q.data?.meta ?? {};
  const rebuildMs = Number(m.last_rebuild_ms ?? 0);
  const unassigned = q.data?.unassigned_cases ?? 0;
  return (
    <section className="surface surface--wide">
      <header className="surface__head">
        <h2 className="surface__title">Index</h2>
        <div className="surface__meta mono">the derived store is a CACHE, not a contract</div>
      </header>

      <div className="surface__body">
        <div className="figs">
          <div className="fig">
            <span className="fig__k">rebuild</span>
            <span className="fig__v mono num">
              {m.last_rebuild_ms ?? "—"}
              {m.last_rebuild_ms === undefined || m.last_rebuild_ms === null ? "" : " ms"}
            </span>
          </div>
          <div className="fig">
            <span className="fig__k">last rebuild</span>
            <span className="fig__v mono">{m.last_rebuild_at ? `${ageSince(m.last_rebuild_at)} ago` : "—"}</span>
          </div>
          <div className="fig">
            <span className="fig__k">cycle</span>
            <span className="fig__v mono num">{m.last_cycle_ms ?? "—"} ms</span>
          </div>
          <div className="fig">
            <span className="fig__k">seq</span>
            <span className="fig__v mono num">{m.seq ?? "—"}</span>
          </div>
        </div>

        {rebuildMs > 60000 && (
          <p className="note">
            <span className="chip chip--down" title="past a minute the derived store has become a liability">
              over the one-minute rebuild budget
            </span>
          </p>
        )}

        <div className="figs">
          <div className="fig">
            <span className="fig__k">parity checked</span>
            <span className="fig__v mono">
              {m.parity_checked_at ? `${ageSince(m.parity_checked_at)} ago` : "never"}
            </span>
          </div>
          <div className="fig">
            <span className="fig__k">mismatches</span>
            <span className="fig__v mono num">{m.parity_mismatches ?? "—"}</span>
          </div>
          <div className="fig">
            <span className="fig__k">torn lines</span>
            <span className="fig__v mono num">{q.data?.torn_lines_total ?? 0}</span>
          </div>
          <div className="fig">
            <span className="fig__k">_unassigned cases</span>
            <span className="fig__v mono num">
              {unassigned ? <span className="chip chip--down">{unassigned}</span> : 0}
            </span>
          </div>
        </div>
        <p className="note" title="a mapping bug made loud: never defaulted to the first tenant">
          an unassigned row is served to nobody and counted here.
        </p>

        <details className="disclose">
          <summary>ingest cursors</summary>
          <VirtualList
            rows={q.data?.cursors ?? []}
            ariaLabel="ingest cursors"
            className="vlist vlist--cursors"
            estimateSize={26}
            rowKey={(c: any) => `${c.source_id}:${c.path}`}
            renderRow={(c: any) => (
              <div className="mono raw__row">
                {c.path} @{c.byte_offset} · rows {c.rows_ingested} · torn {c.torn_lines} · reingests {c.reingests}
              </div>
            )}
            emptyLabel="no cursors"
          />
        </details>

        <div className="row-actions">
          <button onClick={() => rebuild.mutate()} disabled={rebuild.isPending}>
            {rebuild.isPending ? "rebuilding…" : "rebuild from byte 0 (timed)"}
          </button>
          {rebuild.data && <span className="mono num">{rebuild.data.wall_ms} ms</span>}
        </div>
        <p className="note">{q.data?.warning}</p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- tenants */

function Tenants() {
  const q = useQuery({ queryKey: ["admin", "tenants"], queryFn: api.admin.tenants });
  return (
    <section className="surface surface--wide">
      <header className="surface__head">
        <h2 className="surface__title">Tenants</h2>
        <div className="surface__meta mono">roster and ingest mapping, identity only</div>
      </header>

      <div className="surface__body">
        {(q.data?.tenants ?? []).map((t: any) => (
          <div key={t.tenant_id} className="mono">
            {t.tenant_id} · {t.kind} · {t.name ?? ""} {t.tz ? `· ${t.tz}` : ""}
          </div>
        ))}

        <section className="band">
          <div className="band__title">JOURNAL SOURCES</div>
          {(q.data?.journal_sources ?? []).map((s: any) => (
            <div key={s.source_id} className="mono dim">
              {s.source_id} → {s.tenant_id} · {s.root_path} · mode {s.trade_mode ?? "any"} ·{" "}
              {s.enabled ? "enabled" : "disabled"}
            </div>
          ))}
        </section>

        <section className="band">
          <div className="band__title">PRINCIPALS</div>
          {(q.data?.principals ?? []).map((p: any) => (
            <div key={p.principal_id} className="mono dim">
              {p.username} → {p.tenant_id} {p.is_admin ? "· admin" : ""} · last login{" "}
              {p.last_login_at ? ageSince(p.last_login_at) : "never"}
            </div>
          ))}
        </section>

        <p className="note">a second tenant is three INSERTs and zero code changes: tenant, journal_source, principal.</p>
      </div>
    </section>
  );
}

function Updater() {
  const q = useQuery({ queryKey: ["admin", "updater"], queryFn: api.admin.updater });
  return (
    <section className="surface surface--wide">
      <header className="surface__head">
        <h2 className="surface__title">Updater</h2>
      </header>

      <div className="surface__body">
        <div className="figs">
          <div className="fig">
            <span className="fig__k">socket</span>
            <span className="fig__v mono">{q.data?.socket ?? "—"}</span>
          </div>
          <div className="fig">
            <span className="fig__k">state</span>
            <span className={`fig__v mono${q.data?.present ? "" : " dim"}`}>
              {q.data?.present ? "present" : "absent"}
            </span>
          </div>
        </div>
        <p className="note" title="the console holds no git, docker or SSH; it can only speak to the unix socket">
          {q.data?.protocol}
        </p>
      </div>
    </section>
  );
}
